import React, { useState } from 'react';
import styles from './UserList.module.css';
import UserTable from '../UserTable/UserTable';
import UserCard from '../UserCard/UserCard';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import Spinner from '../../common/Spinner/Spinner';
import { debounce } from '../../../utils/helpers';
import { usePermission } from '../../../hooks/usePermissions';

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const UserList = ({
  users = [],
  loading = false,
  onEdit,
  onDelete,
  onView,
  onSearch,
  onRefresh,
  onAddUser,
  children
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const { hasPermission } = usePermission()

  // Debounced search function
  const debouncedSearch = debounce((term) => {
    if (onSearch) {
      onSearch(term);
    }
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
    (user.email && user.email?.toLowerCase().includes(searchTerm?.toLowerCase())) ||
    (user.phone && user.phone?.includes(searchTerm)) ||
    (user.ranumber && user.ranumber?.includes(searchTerm)) ||
    user.userid?.toString().includes(searchTerm)
  );

  if (loading && users.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" text="Loading users..." />
      </div>
    );
  }

  return (
    <div className={styles.userListContainer}>
      <div className={styles.userListHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.listTitle}>User Management</h1>
          <p className={styles.userCount}>
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
          </p>
        </div>

        <div className={styles.headerActions}>
          {hasPermission("CREATE_USERSLIST") && (
            <button
              className={styles.btnAdd}
              onClick={onAddUser}
            >
              + Add New User
            </button>
          )}
        </div>
      </div>

      <div className={styles.filtersSection}>
        <div className={styles.searchSection}>
          <div className={styles.imSearchWrap}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search users by name, email, phone..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          {searchTerm && (
            <button
              type="button"
              className={styles.imBtnSearch}
              onClick={clearSearch}
            >
              Clear
            </button>
          )}
        </div>
      </div>


      {loading && users.length > 0 && (
        <div className={styles.loadingOverlay}>
          <Spinner text="Updating..." />
        </div>
      )}

      <div className={styles.bkTableWrap}>
        <div className={styles.userListContent}>
          {filteredUsers.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>👥</div>
              <h3 className={styles.emptyTitle}>
                {searchTerm ? 'No users found' : 'No users available'}
              </h3>
              <p className={styles.emptyMessage}>
                {searchTerm
                  ? `No users match "${searchTerm}". Try adjusting your search terms.`
                  : 'There are no users to display at the moment.'
                }
              </p>
              {searchTerm && (
                <Button variant="outline" onClick={clearSearch}>
                  Clear Search
                </Button>
              )}
            </div>
          ) : (
            <>
              {viewMode === 'table' ? (
                <>
                  <UserTable
                    users={filteredUsers}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                    canEdit={hasPermission("MODIFY_USERSLIST")}
                      canDelete={hasPermission("DELETE_USERSLIST")}
                      viewKyc={hasPermission("VIEW_USERKYC")}
                  />
                </>
              ) : (
                <div className={`${styles.userGrid} ${styles[viewMode]}`}>
                  {filteredUsers.map((user) => {
                    return (
                      <UserCard
                        key={user.userid}
                        user={user}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onView={onView}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

export default UserList;